import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { AutoplayOverlay } from '@/store/videoFeatures';
import { ColorEditorPopover } from './ColorEditorPopover';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Sparkles, Square, AlertTriangle, Info, Palette, Maximize, PictureInPicture2, MousePointerClick, Zap, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface AutoplayStyleSectionProps {
  autoplayOverlay: AutoplayOverlay;
  updateAutoplayOverlay: (key: keyof AutoplayOverlay, value: any) => void;
  hideTitle?: boolean;
  // Bloqueio de pause é flag top-level (VideoFeatures), não do overlay — entra
  // no grid "Efeitos e Recursos" só quando o setter é passado.
  blockPause?: boolean;
  onBlockPauseChange?: (value: boolean) => void;
}

interface ColorItem {
  key: 'text' | 'icon' | 'background' | 'border';
  label: string;
}

// Função para formatar cor em formato legível
const formatColorValue = (color: string): string => {
  if (color.startsWith('rgba')) {
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      return `${match[1]}, ${match[2]}, ${match[3]}`;
    }
  }
  return color.toUpperCase();
};

export const AutoplayStyleSection: React.FC<AutoplayStyleSectionProps> = ({
  autoplayOverlay,
  updateAutoplayOverlay,
  hideTitle = false,
  blockPause,
  onBlockPauseChange
}) => {
  useLanguage();
  // Trata o default legado do backend (rgba(34, 34, 34, *)) como "sem cor",
  // para que Texto e Ícone apareçam brancos por padrão.
  const isLegacyTextDefault = (val?: string) =>
    !val || /^rgba?\(\s*34\s*,\s*34\s*,\s*34/i.test(val);

  const colors = {
    background: autoplayOverlay.colors?.background || '#ffffff',
    text: isLegacyTextDefault(autoplayOverlay.colors?.text) ? '#ffffff' : autoplayOverlay.colors!.text,
    icon: isLegacyTextDefault(autoplayOverlay.colors?.icon) ? '#ffffff' : autoplayOverlay.colors!.icon,
    border: autoplayOverlay.colors?.border || '#ffffff',
  };

  const colorItems: ColorItem[] = [
    { key: 'background', label: translateInterfaceText("Fundo") },
    { key: 'text', label: translateInterfaceText("Texto") },
    { key: 'icon', label: translateInterfaceText("Ícone") },
    { key: 'border', label: translateInterfaceText("Borda") }
  ];

  const updateColors = (colorKey: string, value: string) => {
    updateAutoplayOverlay('colors', {
      ...colors,
      [colorKey]: value
    });
  };

  // Efeitos do grid — fonte única pros toggles (Detectar Interação, Clique
  // Automático, Pulsar, Exibir borda, Tela cheia, Tela flutuante).
  // detectInteraction e autoClick têm default false (=== true): só vêm ligados
  // quando o usuário salvou explicitamente como true no backend.
  const effects = [
    {
      key: 'detectInteraction',
      icon: MousePointerClick,
      label: translateInterfaceText("Detectar Interação"),
      tooltip: translateInterfaceText("Ativa o som ao detectar ação do visitante (clique, scroll ou toque)"),
      checked: autoplayOverlay.detectInteraction === true,
      onChange: (value: boolean) => updateAutoplayOverlay('detectInteraction', value),
    },
    {
      key: 'autoClick',
      icon: Zap,
      label: translateInterfaceText("Clique Automático"),
      tooltip: translateInterfaceText("Inicia o vídeo com som sozinho, sem esperar o visitante clicar no aviso do autoplay. Navegadores só liberam som automático para quem já tem histórico na página — quando bloquearem, o aviso continua na tela e o primeiro toque em qualquer lugar libera o som."),
      checked: autoplayOverlay.autoClick === true,
      onChange: (value: boolean) => updateAutoplayOverlay('autoClick', value),
    },
    {
      key: 'pulseAnimation',
      icon: Sparkles,
      label: translateInterfaceText("Pulsar"),
      tooltip: translateInterfaceText("Chama atenção sem poluir"),
      checked: autoplayOverlay.pulseAnimation || false,
      onChange: (value: boolean) => updateAutoplayOverlay('pulseAnimation', value),
    },
    {
      key: 'whiteBorder',
      icon: Square,
      label: translateInterfaceText("Exibir borda"),
      tooltip: translateInterfaceText("Destaque sutil do overlay"),
      checked: autoplayOverlay.whiteBorder || false,
      onChange: (value: boolean) => updateAutoplayOverlay('whiteBorder', value),
    },
    {
      key: 'haveFullScreen',
      icon: Maximize,
      label: translateInterfaceText("Tela cheia"),
      tooltip: translateInterfaceText("Abre o vídeo em tela cheia ao clicar no overlay"),
      checked: autoplayOverlay.haveFullScreen || false,
      onChange: (value: boolean) => updateAutoplayOverlay('haveFullScreen', value),
    },
    {
      key: 'havePictureInPicture',
      icon: PictureInPicture2,
      label: translateInterfaceText("Tela flutuante"),
      tooltip: translateInterfaceText("Quando o visitante rola a página e o vídeo sai da tela, ele vira uma caixinha no canto e continua tocando. Ao rolar de volta, o vídeo retorna ao lugar de origem."),
      checked: autoplayOverlay.havePictureInPicture || false,
      onChange: (value: boolean) => updateAutoplayOverlay('havePictureInPicture', value),
    },
    ...(onBlockPauseChange
      ? [{
          key: 'blockPause',
          icon: Lock,
          label: translateInterfaceText("Bloquear pause"),
          tooltip: translateInterfaceText("Impede o espectador de pausar o vídeo"),
          checked: blockPause || false,
          onChange: onBlockPauseChange,
        }]
      : []),
  ];

  // Mostrar aviso se modelo ≠ "Personalizado"
  const showWarning = autoplayOverlay.useTemplate && autoplayOverlay.templateId !== 'custom';

  return (
    <div className="space-y-3">
      {/* Accordion: Estilo Avançado - Padrão PlayerThemeCard */}
      <Accordion type="single" collapsible defaultValue="style-advanced" className="w-full">
        <AccordionItem
          value="style-advanced"
          className="bg-white rounded-[12px] border border-[#EAEAEA] overflow-hidden shadow-sm"
        >
          {/* AccordionTrigger igual PlayerThemeCard */}
          <AccordionTrigger className="px-4 py-3.5 hover:no-underline hover:bg-[#F7F8FA] transition-colors">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center bg-[#1D4FFF]/10 text-[#1D4FFF]">
                <Palette className="w-4 h-4" strokeWidth={2} />
              </div>
              <div className="text-left">
                <span className="text-[13px] font-semibold text-[#1A1A1A] block leading-tight">{translateInterfaceText("Estilo Avançado")}</span>
                <span className="text-[11px] text-[#6B7280] block leading-tight">{translateInterfaceText("Personalize a aparência do Autoplay")}</span>
              </div>
            </div>
          </AccordionTrigger>

          <AccordionContent className="px-4 pb-4 pt-2">
            <div className="space-y-5">
              {/* Seção: Cores - Grid 2x2 Premium com swatch + label + valor */}
              <div className="space-y-3">
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  {translateInterfaceText("Cores")}</span>
                <div className="grid grid-cols-2 gap-3">
                  {colorItems.map(item => (
                    <ColorEditorPopover
                      key={item.key}
                      colorName={item.label}
                      currentColor={colors[item.key] || '#ffffff'}
                      onSave={(newColor) => updateColors(item.key, newColor)}
                    >
                      <button
                        type="button"
                        className="flex items-center gap-3 p-3 rounded-xl border border-border bg-background cursor-pointer"
                      >
                        {/* Swatch circular */}
                        <div
                          className="w-8 h-8 rounded-full border-2 border-border/60 shadow-sm flex-shrink-0"
                          style={{ backgroundColor: colors[item.key] || '#ffffff' }}
                        />

                        {/* Label + Valor */}
                        <div className="text-left min-w-0 flex-1">
                          <span className="text-sm font-medium text-foreground block">
                            {item.label}
                          </span>
                          <span className="text-[10px] text-muted-foreground block truncate">
                            {formatColorValue(colors[item.key] || '#ffffff')}
                          </span>
                        </div>
                      </button>
                    </ColorEditorPopover>
                  ))}
                </div>
              </div>

              {/* Aviso de sobrescrita (quando aplicável) - Azul claro padrão */}
              {showWarning && (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#F0F7FF] border border-[#D4E5FF]">
                  <Info className="w-4 h-4 text-[#1F6BFF] flex-shrink-0" />
                  <span className="text-xs text-[#1C1C1E]">
                    {translateInterfaceText("Este ajuste sobrescreve o modelo selecionado")}</span>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Card separado: Efeitos e Recursos — lista vertical dos toggles */}
      <div className="bg-white rounded-[12px] border border-[#EAEAEA] shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center bg-[#1D4FFF]/10 text-[#1D4FFF]">
            <Sparkles className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className="text-left">
            <span className="text-[13px] font-semibold text-[#1A1A1A] block leading-tight">{translateInterfaceText("Efeitos e Recursos")}</span>
            <span className="text-[11px] text-[#6B7280] block leading-tight">{translateInterfaceText("Ative comportamentos extras do Autoplay")}</span>
          </div>
        </div>

        <div className="space-y-2">
          {effects.map(({ key, icon: Icon, label, tooltip, checked, onChange }) => (
            <div
              key={key}
              className="flex items-center justify-between p-3 rounded-xl border border-border bg-background"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Icon className={cn(
                  "h-4 w-4 flex-shrink-0 transition-colors",
                  checked ? "text-primary" : "text-muted-foreground"
                )} />
                {/* Sem truncate: rótulos longos quebram em duas linhas em vez
                    de virar reticências. leading-snug evita que a segunda linha
                    estique demais a altura do item. */}
                <span className="text-sm font-medium text-foreground leading-snug">{label}</span>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground/60 cursor-help flex-shrink-0" />
                    </TooltipTrigger>
                    {/* maxWidth inline, e não via classe: o index.css tem
                        `[data-radix-portal] * { max-width: none }`, que anula
                        qualquer max-w-* utilitário aqui dentro (o tooltip é
                        renderizado num portal do Radix). Sem isso o texto
                        estica numa linha só. */}
                    <TooltipContent
                      side="top"
                      className="text-xs leading-snug"
                      style={{ maxWidth: 220, whiteSpace: 'normal', overflowWrap: 'break-word' }}
                    >
                      {tooltip}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Switch
                checked={checked}
                onCheckedChange={onChange}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
